import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Sparkles, Award } from 'lucide-react';
import { format, startOfWeek, eachWeekOfInterval, subDays } from 'date-fns';

interface ThoughtEntry {
  id: string;
  created_at: string;
  cognitive_distortions: string[];
  alternative_thought?: string | null;
}

interface DistortionProgressChartProps {
  entries: ThoughtEntry[];
}

export function DistortionProgressChart({ entries }: DistortionProgressChartProps) {
  const progressData = useMemo(() => {
    if (entries.length < 5) return null;

    // Group entries by week
    const now = new Date();
    const startDate = subDays(now, 56); // 8 weeks
    
    const weeks = eachWeekOfInterval({
      start: startDate,
      end: now
    });

    const weeklyData = weeks.map(weekStart => {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      
      const weekEntries = entries.filter(e => {
        const entryDate = new Date(e.created_at);
        return entryDate >= weekStart && entryDate < weekEnd;
      });

      const totalDistortions = weekEntries.reduce((sum, e) => sum + (e.cognitive_distortions?.length || 0), 0);
      const reframedCount = weekEntries.filter(e => e.alternative_thought).length;
      const avgDistortions = weekEntries.length > 0 ? totalDistortions / weekEntries.length : 0;
      const reframeRate = weekEntries.length > 0 ? (reframedCount / weekEntries.length) * 100 : 0;

      return {
        week: format(weekStart, 'MMM d'),
        thoughts: weekEntries.length,
        avgDistortions: Math.round(avgDistortions * 10) / 10,
        reframeRate: Math.round(reframeRate),
        totalDistortions
      };
    });

    return weeklyData.filter(w => w.thoughts > 0);
  }, [entries]);

  const trendAnalysis = useMemo(() => {
    if (!progressData || progressData.length < 3) return null;

    const recentWeeks = progressData.slice(-3);
    const olderWeeks = progressData.slice(0, -3);

    if (olderWeeks.length === 0) return null;

    const recentAvgDistortions = recentWeeks.reduce((sum, w) => sum + w.avgDistortions, 0) / recentWeeks.length;
    const olderAvgDistortions = olderWeeks.reduce((sum, w) => sum + w.avgDistortions, 0) / olderWeeks.length;
    
    const recentReframeRate = recentWeeks.reduce((sum, w) => sum + w.reframeRate, 0) / recentWeeks.length;
    const olderReframeRate = olderWeeks.reduce((sum, w) => sum + w.reframeRate, 0) / olderWeeks.length;

    const distortionChange = olderAvgDistortions > 0 
      ? ((recentAvgDistortions - olderAvgDistortions) / olderAvgDistortions) * 100 
      : 0;
    
    const reframeChange = recentReframeRate - olderReframeRate;

    return {
      distortionChange: Math.round(distortionChange),
      reframeChange: Math.round(reframeChange),
      improving: distortionChange < -10 || reframeChange > 10,
      needsAttention: distortionChange > 20 && reframeChange < -10
    };
  }, [progressData]);

  if (!progressData || progressData.length < 2) {
    return (
      <Card className="bg-gradient-to-br from-muted/30 to-muted/10">
        <CardContent className="pt-6 text-center">
          <Sparkles className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground mb-2">Keep logging to see your progress</p>
          <p className="text-sm text-muted-foreground/70">
            Progress tracking becomes available after logging thoughts over at least 2 weeks
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Trend Summary */}
      {trendAnalysis && (
        <Card className={`border-2 ${
          trendAnalysis.improving 
            ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-green-500/20' 
            : trendAnalysis.needsAttention
              ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/20'
              : 'bg-gradient-to-br from-primary/10 to-accent/5 border-primary/20'
        }`}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              {trendAnalysis.improving ? (
                <>
                  <div className="p-3 rounded-full bg-green-500/20">
                    <Award className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-green-700 dark:text-green-400">
                      You're Making Progress!
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {trendAnalysis.distortionChange < -10 && 
                        `Your cognitive distortions have reduced by ${Math.abs(trendAnalysis.distortionChange)}%. `}
                      {trendAnalysis.reframeChange > 10 && 
                        `Your reframe rate has improved by ${trendAnalysis.reframeChange}%. `}
                      This is real, meaningful change. Keep going!
                    </p>
                  </div>
                </>
              ) : trendAnalysis.needsAttention ? (
                <>
                  <div className="p-3 rounded-full bg-amber-500/20">
                    <Sparkles className="w-8 h-8 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-amber-700 dark:text-amber-400">
                      A Gentle Check-In
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Your patterns have shifted recently - you might be going through a challenging time. 
                      This is information, not judgment. Consider reaching out to Therappi for extra support.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-3 rounded-full bg-primary/20">
                    <TrendingUp className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Consistent Awareness</h3>
                    <p className="text-sm text-muted-foreground">
                      You're consistently tracking your thoughts. This awareness itself is therapeutic.
                      Patterns take time to shift - you're building the foundation.
                    </p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Distortions Over Time */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Average Distortions Per Thought
              {trendAnalysis?.distortionChange && trendAnalysis.distortionChange < 0 && (
                <Badge variant="secondary" className="bg-green-500/20 text-green-700 dark:text-green-400">
                  <TrendingDown className="w-3 h-3 mr-1" />
                  {Math.abs(trendAnalysis.distortionChange)}%
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Lower is better - fewer distortions per thought shows clearer thinking
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={progressData}>
                <defs>
                  <linearGradient id="colorDistortions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="week" className="text-xs" />
                <YAxis domain={[0, 'auto']} className="text-xs" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="avgDistortions" 
                  stroke="hsl(var(--chart-1))" 
                  fill="url(#colorDistortions)"
                  strokeWidth={2}
                  name="Avg Distortions"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Reframe Rate */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Reframe Rate
              {trendAnalysis?.reframeChange && trendAnalysis.reframeChange > 0 && (
                <Badge variant="secondary" className="bg-green-500/20 text-green-700 dark:text-green-400">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  +{trendAnalysis.reframeChange}%
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Higher is better - more reframed thoughts shows active CBT practice
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={progressData}>
                <defs>
                  <linearGradient id="colorReframe" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-3))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--chart-3))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="week" className="text-xs" />
                <YAxis domain={[0, 100]} unit="%" className="text-xs" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value: number) => [`${value}%`, 'Reframe Rate']}
                />
                <Area 
                  type="monotone" 
                  dataKey="reframeRate" 
                  stroke="hsl(var(--chart-3))" 
                  fill="url(#colorReframe)"
                  strokeWidth={2}
                  name="Reframe Rate"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Weekly Thought Tracking Activity</CardTitle>
          <CardDescription>
            Consistency builds awareness - even 2-3 entries per week can reveal patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={150}>
            <LineChart data={progressData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="week" className="text-xs" />
              <YAxis className="text-xs" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="thoughts" 
                stroke="hsl(var(--primary))" 
                strokeWidth={2}
                dot={{ fill: 'hsl(var(--primary))' }}
                name="Thoughts Logged"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
